import GroupMemberRecord from "models/view-models/group-member-record";
import SupportUserSubscriptionRecord from "models/view-models/support-user-subscription-record";
import { useEffect, useState } from "react";
import { CollectionUtils } from "utilities/collection-utils";
import { useManagePendingInvites } from "utilities/hooks/domain/team-onboarding/use-manage-pending-invites";
import { t } from "utilities/localization-utils";
import GroupMemberService from "utilities/services/groups/group-member-service";
import { ToastManager } from "utilities/toast/toast-manager";
const TEAM_OWNER_COUNT = 1;
const useSupportManageTeamMembers = (
    sourceGroupId: number | undefined,
    subscription: SupportUserSubscriptionRecord,
    ownerExternalId: number | string | undefined
) => {
    const [emailsToInvite, setEmailsToInvite] = useState<string[]>([]);
    const [teamMembersToMigrate, setTeamMembersToMigrate] = useState<
        GroupMemberRecord[]
    >([]);
    const [emailError, setEmailError] = useState<string | undefined>(undefined);
    const [isLoading, setIsLoading] = useState(false);
    const [targetTeamExistingMemberCount, setTargetTeamExistingMemberCount] =
        useState(0);

    const isOwnerPartOfGroup = teamMembersToMigrate.some(
        (member) => member.legacyId === ownerExternalId
    );
    const totalSeatsCurrentlyTaken =
        targetTeamExistingMemberCount +
        teamMembersToMigrate.length +
        emailsToInvite.length +
        (isOwnerPartOfGroup ? 0 : TEAM_OWNER_COUNT);

    const totalLicenses = subscription.totalLicenses ?? 0;
    const totalSeatsRemaining = totalLicenses - totalSeatsCurrentlyTaken;

    const teamMigrationSummary = {
        seatsRemaining: totalSeatsRemaining,
        seatsTaken: totalSeatsCurrentlyTaken,
        membersToAdd:
            teamMembersToMigrate.length +
            (isOwnerPartOfGroup ? 0 : TEAM_OWNER_COUNT),
        existingMembers: targetTeamExistingMemberCount,
    };

    const { handleAddClick, handleOnChange, inviteEmail } =
        useManagePendingInvites({
            totalLicenses: totalLicenses,
            teamMembersToMigrate: teamMembersToMigrate,
            emailsToInvite: emailsToInvite,
            onUpdateEmailsToInvite: setEmailsToInvite,
            onEmailError: (error) => {
                setEmailError(error);
            },
            totalSeatsCurrentlyTaken: totalSeatsCurrentlyTaken,
        });

    const { list: getGroupMembersApi } = GroupMemberService.useList();

    useEffect(() => {
        if (sourceGroupId && subscription.groupId) {
            const targetGroupId = subscription.groupId;
            const listGroupMembersApi = async () => {
                setIsLoading(true);
                try {
                    const promises = [
                        getGroupMembersApi({
                            groupId: sourceGroupId,
                        }),
                        getGroupMembersApi({
                            groupId: targetGroupId,
                        }),
                    ];

                    const [sourceResult, targetResult] =
                        await Promise.all(promises);

                    setTargetTeamExistingMemberCount(
                        targetResult.resultObjects?.length ?? 0
                    );
                    if (CollectionUtils.isEmpty(sourceResult.resultObjects)) {
                        return;
                    }
                    setTeamMembersToMigrate(sourceResult.resultObjects);
                } catch (error) {
                    ToastManager.error(
                        t("errors-actionResource", {
                            action: t("loading"),
                            resource: t("team-member_plural"),
                        })
                    );
                } finally {
                    setIsLoading(false);
                }
            };

            listGroupMembersApi();
        }
    }, [getGroupMembersApi, sourceGroupId, subscription.groupId]);

    return {
        setTeamMembersToMigrate,
        setEmailsToInvite,
        teamMembersToMigrate,
        emailsToInvite,
        emailError,
        handleOnChange,
        handleAddClick,
        inviteEmail,
        isLoading,
        teamMigrationSummary,
    };
};

export default useSupportManageTeamMembers;
